import React, {
    FormEvent,
    useCallback,
    useDeferredValue,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";
import ReactFocusLock from "react-focus-lock";
import { ProductSearchBuilder, ProductSearchResponse } from "@relewise/client";
import { useDebounce } from "react-use";
import { useAppData } from "@danishagro/shared/src/contexts/appData.context";
import { useTranslations } from "@danishagro/shared/src/contexts/translations/translations.context";
import { DA_Icon, DA_IconNames } from "@danishagro/shared/src/components/atoms/Icon/Icon.component";
import { useOnClickOutside } from "@danishagro/shared/src/hooks/useOnClickOutside.hook";
import { blurActiveElement } from "@danishagro/shared/src/helpers/blurActiveElement.helper";
import { useBottomSheet } from "@danishagro/shared/src/contexts/bottomSheet/bottomSheet.context";
import { useSiteHeader } from "@danishagro/shared/src/contexts/siteHeader.context";
import { DA_Spinner } from "@danishagro/shared/src/components/atoms/Spinner/Spinner.component";
import { useRelewise } from "@danishagro/shared/src/contexts/relewise.context";
import { DA_Text } from "@danishagro/shared/src/components/atoms/Text/Text.component";
import {
    DA_EmptyState,
    DA_EmptyStateTypes,
} from "@danishagro/shared/src/components/molecules/EmptyState/EmptyState.component";
import { useScreen } from "@danishagro/shared/src/contexts/screen.context";
import { DA_ButtonLink } from "@danishagro/shared/src/components/atoms/ButtonLink/ButtonLink.component";
import { useDynamicOverlay } from "@danishagro/shared/src/hooks/dynamicOverlay/useDynamicOverlay.hook";
import { formatNumber } from "@danishagro/shared/src/helpers/formatNumber.helper";
import { DA_Tag } from "@danishagro/shared/src/components/atoms/Tag/Tag.component";
import { cn } from "@danishagro/shared/src//helpers/classNames.helper";
import { _SearchProduct } from "./SearchProduct/SearchProduct.component";
import { RelewiseSearchProduct } from "./interfaces/relewiseSearchProduct.interface";
import { _SearchPage } from "./SearchPage/SearchPage.component";
import S from "./SearchInputB2c.module.scss";

interface DA_SearchInputB2cProps {
    trapFocus?: boolean;
    smallHeight?: boolean;
}

interface ContentResponse {
    alias: string;
    name: string;
    url: string;
    rootNodeId: string;
    urlSegments: string;
}

type SearchFunction = (
    searchTerm: string,
    options?: { maxProducts?: number },
    fetchOptions?: RequestInit
) => Promise<ContentResponse[]>;

const DA_SearchInputB2c = (props: DA_SearchInputB2cProps) => {
    const [searchTerm, setSearchTerm] = useState("");
    const deferredSearchTerm = useDeferredValue(searchTerm);
    const [viewState, setViewState] = useState<string>("NONE");
    const [results, setResults] = useState([]);
    const [relewiseResults, setRelewiseResults] = useState<RelewiseSearchProduct[]>();
    const [totalResults, setTotalResults] = useState(0);
    const { siteVariant, currentSite, currentCulture } = useAppData();
    const { getDictionaryString } = useTranslations();
    const location = useLocation();
    const wrapperRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);
    const controllerRef = useRef<AbortController | null>();
    const navigate = useNavigate();
    const { closeOverlay } = useDynamicOverlay();

    const [focusedColumn, setFocusedColumn] = useState<"content" | "product" | "">("");
    const [focusedIndex, setFocusedIndex] = useState(0);
    const [hasMoved, setHasMoved] = useState<boolean>(false);

    const listRefContent = useRef<HTMLUListElement | null>(null);
    const listRefProduct = useRef<HTMLUListElement | null>(null);

    const { dismissBottomSheet } = useBottomSheet();
    const { togglePopUp } = useSiteHeader();
    const { isMobileOrTablet } = useScreen();

    const formRef = useRef<HTMLFormElement>(null);

    const { getSearcher, relewiseUser } = useRelewise();

    const API_URL = globalThis.b2cFrontendUrl;

    const settings = {
        language: "da",
        currency: "DKK",
        displayedAtLocation: "Relewise Demo Store",
        user: relewiseUser(),
    };

    const builder = new ProductSearchBuilder(settings)
        .setSelectedProductProperties({
            displayName: true,
            pricing: true,
            allData: true,
            dataKeys: ["*"],
        })
        .setExplodedVariants(1)
        .setTerm(searchTerm);

    const clearSearch = useCallback(() => {
        setSearchTerm("");
        setResults(undefined);
        setTotalResults(0);
        setViewState("NONE");
    }, []);

    const onResultClicked = useCallback(() => {
        clearSearch();

        dismissBottomSheet();
        togglePopUp(undefined);
    }, [clearSearch, dismissBottomSheet, togglePopUp]);

    const reset = useCallback(() => {
        clearSearch();
        inputRef.current.value = "";
    }, [clearSearch]);

    useEffect(() => {
        if (inputRef.current.value === "") {
            setFocusedColumn("");
            setFocusedIndex(0);
            setHasMoved(false);
        }
    }, [searchTerm]);

    const onFocus = useCallback(() => {
        if (inputRef.current?.value) {
            setSearchTerm(inputRef.current.value);
        }
    }, []);

    useOnClickOutside(
        wrapperRef,
        useCallback(() => {
            reset();
            setTimeout(() => blurActiveElement(), 10);
        }, [reset]),
        [totalResults > 0]
    );

    const contentSearch: SearchFunction = useCallback(
        async (searchTerm, options?, fetchOptions?) => {
            const request = await fetch(
                `${API_URL}/api/search?term=${searchTerm.trim()}&pageSize=${
                    options?.maxProducts || 10
                }`,
                {
                    ...fetchOptions,
                    credentials: "include",
                }
            );

            const response = await request.json();
            return response;
        },
        [API_URL]
    );

    const [loadingContent, setLoadingContent] = useState(false);
    const [loadingRelewise, setLoadingRelewise] = useState(false);

    const [announcement, setAnnouncement] = useState("");
    const [contentResultsCount, setContentResultsCount] = useState<number | null>(null);
    const [productResultsCount, setProductResultsCount] = useState<number | null>(null);

    useDebounce(
        async () => {
            if (controllerRef.current) {
                // If a request has already been sent to the API,
                // abort it before sending a new one
                controllerRef.current.abort();
            }

            if (deferredSearchTerm) {
                setLoadingContent(true);
                setLoadingRelewise(true);
                controllerRef.current = new AbortController();

                contentSearch(
                    deferredSearchTerm,
                    { maxProducts: 10 },
                    { signal: controllerRef.current.signal }
                )
                    .then((response) => {
                        setResults(response);
                        setContentResultsCount(response.length);
                    })
                    .finally(() => {
                        setLoadingContent(false);
                    });

                getSearcher()
                    .searchProducts(builder.build())
                    .then((response: ProductSearchResponse) => {
                        if (response && response.results.length > 0) {
                            const count = response?.results?.length || 0;
                            setRelewiseResults(response.results as RelewiseSearchProduct[]);
                            setTotalResults(response.hits);
                            setProductResultsCount(count);
                        } else {
                            setRelewiseResults([]);
                            setTotalResults(0);
                        }
                    })
                    .catch((err) => {
                        console.error(err);
                        // Ignore abort errors, since they only occur
                        // when we initiate them ourselves (a few lines up)
                        if (err?.name !== "AbortError") {
                            setViewState("ERROR");
                            console.log(err);
                        }
                    })
                    .finally(() => {
                        setLoadingRelewise(false);
                        setViewState("DONE");
                    });
            } else {
                setViewState("NONE");
                setResults(undefined);
                setTotalResults(0);
            }
        },
        250,
        [deferredSearchTerm]
    );

    useEffect(() => {
        if (contentResultsCount !== null && productResultsCount !== null) {
            const contentText =
                contentResultsCount > 0
                    ? contentResultsCount > 1
                        ? `${getDictionaryString(
                              `accessibility.screenReader.searchOnContent.moreThanOneResult`,
                              {
                                  contentResults: contentResultsCount,
                              }
                          )}`
                        : `${getDictionaryString(
                              `accessibility.screenReader.searchOnContent.oneResult`,
                              {
                                  contentResults: contentResultsCount,
                              }
                          )}`
                    : getDictionaryString(`accessibility.screenReader.searchOnContent.noResults`);

            const productText =
                productResultsCount > 0
                    ? productResultsCount > 1
                        ? `${getDictionaryString(
                              `accessibility.screenReader.searchOnProducts.moreThanOneResult`,
                              {
                                  productResults: productResultsCount,
                              }
                          )}`
                        : `${getDictionaryString(
                              `accessibility.screenReader.searchOnProducts.oneResult`,
                              {
                                  productResults: productResultsCount,
                              }
                          )}`
                    : getDictionaryString(`accessibility.screenReader.searchOnProducts.noResults`);

            const text = `${contentText}. ${productText}.`;

            setAnnouncement(text);

            // Reset so it doesn’t re-announce unnecessarily
            setContentResultsCount(null);
            setProductResultsCount(null);
        }
    }, [contentResultsCount, getDictionaryString, productResultsCount]);

    // Reset search on location change
    useEffect(() => {
        reset();
    }, [location, reset]);

    const searchPlaceholderText = useMemo(() => {
        switch (currentSite) {
            case "CMS":
                return getDictionaryString("General.SearchPlaceholderCms");
            case "MYFARM":
                return getDictionaryString("searchPlaceholderMyfarm");
            case "B2B":
                return getDictionaryString("searchPlaceholderB2b");
            case "B2C":
                return getDictionaryString("header.search.inputPlaceholder");
            default:
                return getDictionaryString("searchPlaceholder");
        }
    }, [currentSite, getDictionaryString]);

    const EmptyState = () => (
        <DA_EmptyState
            type={DA_EmptyStateTypes.NoResults}
            title={getDictionaryString("general.noResults.title")}
            text={getDictionaryString("general.noResults.text")}
        />
    );

    const redirectToSearchPage = useCallback(() => {
        closeOverlay();
        navigate(`/searchresults?term=${searchTerm}`);
    }, [closeOverlay, navigate, searchTerm]);

    const onSubmit = useCallback(
        (event: FormEvent<HTMLFormElement>) => {
            event.preventDefault();
            redirectToSearchPage();
        },
        [redirectToSearchPage]
    );

    const displayedProducts = relewiseResults?.slice(0, isMobileOrTablet ? 5 : undefined);

    const [urlState, setUrlState] = useState<string | null>(null);

    const navigateWithKeys = useCallback(
        (event: React.KeyboardEvent) => {
            if (event.key === "Escape") {
                reset();
                setHasMoved(false);
                setFocusedIndex(0);
                return;
            }

            if (viewState !== "DONE") return;

            const contentCount = results?.length || 0;
            const productCount = displayedProducts?.length || 0;

            const currentList = focusedColumn === "content" ? contentCount : productCount;

            const updateUrlFromCurrentFocus = (column: "product" | "content", index: number) => {
                const listElement =
                    column === "content" ? listRefContent.current : listRefProduct.current;

                if (!listElement) return;

                const listItems = listElement.querySelectorAll("li");
                const item = listItems[index];
                const link = item?.querySelector("a");
                const href = link?.getAttribute("href");

                if (href) {
                    const url = new URL(href, window.location.href);
                    const pathname = url.pathname;

                    setUrlState(pathname);
                    // Create a new URL object from the href, then get the pathname (the part after the domain)
                }
            };

            if (event.key === "ArrowDown") {
                event.preventDefault();

                if (!hasMoved) {
                    setHasMoved(true);
                    if (productCount > 0) {
                        setFocusedColumn("product");
                        setFocusedIndex(0);
                        updateUrlFromCurrentFocus("product", 0);
                    } else if (contentCount > 0) {
                        setFocusedColumn("content");
                        setFocusedIndex(0);
                        updateUrlFromCurrentFocus("content", 0);
                    }
                    return;
                }

                setFocusedIndex((prev) => {
                    const next = prev + 1;
                    const newIndex = next >= currentList ? 0 : next;

                    if (focusedColumn === "product" || focusedColumn === "content") {
                        updateUrlFromCurrentFocus(focusedColumn, newIndex);
                    }

                    return newIndex;
                });
            }

            if (event.key === "ArrowUp") {
                event.preventDefault();

                if (!hasMoved) {
                    setHasMoved(true);
                    if (productCount > 0) {
                        setFocusedColumn("product");
                        setFocusedIndex(productCount - 1);
                        updateUrlFromCurrentFocus("product", productCount - 1);
                    } else if (contentCount > 0) {
                        setFocusedColumn("content");
                        setFocusedIndex(contentCount - 1);
                        updateUrlFromCurrentFocus("content", contentCount - 1);
                    }
                    return;
                }

                setFocusedIndex((prev) => {
                    const next = prev - 1;
                    const newIndex = next < 0 ? currentList - 1 : next;

                    if (focusedColumn === "product" || focusedColumn === "content") {
                        updateUrlFromCurrentFocus(focusedColumn, newIndex);
                    }

                    return newIndex;
                });
            }

            if (event.key === "ArrowLeft") {
                event.preventDefault();
                if (focusedColumn === "product" && contentCount > 0) {
                    setFocusedColumn("content");
                    setFocusedIndex(0);
                    updateUrlFromCurrentFocus("content", 0);
                }
            }

            if (event.key === "ArrowRight") {
                event.preventDefault();
                if (focusedColumn === "content" && productCount > 0) {
                    setFocusedColumn("product");
                    setFocusedIndex(0);
                    updateUrlFromCurrentFocus("product", 0);
                }
            }

            if (event.key === "Enter") {
                if (hasMoved && urlState) {
                    event.preventDefault();
                    onResultClicked();
                    navigate(urlState);
                    reset();
                    setTimeout(() => blurActiveElement(), 10);
                } else {
                    console.warn("No valid URL to navigate to.");
                }
            }
        },
        [
            viewState,
            results?.length,
            displayedProducts?.length,
            focusedColumn,
            reset,
            hasMoved,
            urlState,
            onResultClicked,
            navigate,
        ]
    );

    useEffect(() => {
        function handleClickOutside(event: MouseEvent) {
            if (formRef.current && !formRef.current.contains(event.target as Node)) {
                setFocusedIndex(0);
                setFocusedColumn(null); // Optional: or keep current column
                setHasMoved(false);
            }
        }

        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);

    return (
        <ReactFocusLock disabled={!props.trapFocus || totalResults === 0 || false}>
            {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions */}
            <div className={S.wrapper} ref={wrapperRef} onKeyDown={navigateWithKeys}>
                <div
                    role="status"
                    aria-live="polite"
                    style={{ position: "absolute", left: "-9999px", height: 0, overflow: "hidden" }}
                >
                    {announcement}
                </div>
                <form role="search" className={S.form} ref={formRef} onSubmit={onSubmit}>
                    <input
                        type="search"
                        name="term"
                        className={cn(S.input, props.smallHeight && S.inputSmallHeight)}
                        placeholder={searchPlaceholderText}
                        onChange={({ target }) => setSearchTerm(target.value)}
                        onFocus={onFocus}
                        autoComplete="off"
                        ref={inputRef}
                    />

                    {deferredSearchTerm ? (
                        <button
                            type="button"
                            className={S.button}
                            onClick={reset}
                            aria-label={getDictionaryString("accessibility.search.clearSearch")}
                        >
                            <DA_Icon name={DA_IconNames.Close} className={S.clearIcon} />
                        </button>
                    ) : (
                        <button
                            type="button"
                            className={S.button}
                            onClick={() => inputRef.current?.focus()}
                            tabIndex={-1}
                            aria-label={getDictionaryString("accessibility.search.searchButton")}
                        >
                            <DA_Icon name={DA_IconNames.Zoom} className={S.searchIcon} />
                        </button>
                    )}
                </form>

                <div className={S.incrementalSearchResults}>
                    {viewState === "DONE" ? (
                        <div
                            className={cn(
                                S.resultsContainer,
                                siteVariant.alias === "hatting-agro-b2b" && S.searchNoContent
                            )}
                        >
                            {siteVariant.alias !== "hatting-agro-b2b" && (
                                <>
                                    <div className={S.resultsColumn1}>
                                        <div className={S.resultColumnHeader}>
                                            <DA_Text noPadding smallText>
                                                {getDictionaryString(
                                                    "searchInput.sectionContentTitle"
                                                )}
                                            </DA_Text>
                                        </div>
                                        <ul
                                            className={S.productList}
                                            role="listbox"
                                            ref={listRefContent}
                                        >
                                            {loadingContent ? (
                                                <div className={S.loadingSpinner}>
                                                    <DA_Spinner />
                                                </div>
                                            ) : results?.length > 0 ? (
                                                results?.map((page, index) => {
                                                    const isFocused =
                                                        focusedColumn === "content" &&
                                                        focusedIndex === index;
                                                    return (
                                                        <li
                                                            key={index}
                                                            className={isFocused ? S.focused : ""}
                                                            role="option"
                                                            aria-selected={focusedIndex === index}
                                                        >
                                                            <_SearchPage
                                                                highlightedString={searchTerm}
                                                                onResultClicked={onResultClicked}
                                                                {...page}
                                                            />
                                                        </li>
                                                    );
                                                })
                                            ) : (
                                                <EmptyState />
                                            )}
                                        </ul>
                                    </div>
                                    {!isMobileOrTablet && <div className={S.divider} />}
                                </>
                            )}

                            <div className={S.resultsColumn2}>
                                <div className={S.resultColumnHeader}>
                                    <DA_Text noPadding smallText>
                                        {getDictionaryString("searchInput.sectionProductsTitle")}
                                    </DA_Text>
                                    <DA_ButtonLink
                                        href={`/searchresults?term=${searchTerm}`}
                                        className={S.showAllItems}
                                        onClick={() => redirectToSearchPage()}
                                        title={getDictionaryString("searchInput.viewAllProducts")}
                                    />
                                </div>
                                <ul className={S.productList} ref={listRefProduct}>
                                    {loadingRelewise ? (
                                        <div className={S.loadingSpinner}>
                                            <DA_Spinner />
                                        </div>
                                    ) : displayedProducts?.length > 0 ? (
                                        displayedProducts?.map((product, index) => {
                                            const isFocused =
                                                focusedColumn === "product" &&
                                                focusedIndex === index;
                                            const price = product.salesPrice || product.listPrice;
                                            return (
                                                <li
                                                    key={product.productId}
                                                    className={isFocused ? S.focused : ""}
                                                    role="option"
                                                    aria-selected={focusedIndex === index}
                                                >
                                                    <div className={S.productListItem}>
                                                        <_SearchProduct
                                                            highlightedString={searchTerm}
                                                            onResultClicked={onResultClicked}
                                                            {...product}
                                                        />
                                                        <div className={S.productListItemPrice}>
                                                            {product?.data?.hasDiscount?.value && (
                                                                <DA_Tag color="yellow" size="small">
                                                                    {getDictionaryString(
                                                                        "general.onSale"
                                                                    )}
                                                                </DA_Tag>
                                                            )}
                                                            {formatNumber(price, currentCulture, {
                                                                decimals: 2,
                                                            })}
                                                        </div>
                                                    </div>
                                                </li>
                                            );
                                        })
                                    ) : (
                                        <EmptyState />
                                    )}
                                </ul>
                            </div>
                        </div>
                    ) : null}

                    {/** Loading */}
                    {viewState === "LOADING" ? (
                        <div className={S.statusContainer}>
                            <DA_Spinner />
                        </div>
                    ) : null}

                    {/** Error */}
                    {viewState === "ERROR" ? (
                        <div className={S.statusContainer}>
                            {getDictionaryString("general.searchError")}
                        </div>
                    ) : null}
                </div>
            </div>
        </ReactFocusLock>
    );
};

export default DA_SearchInputB2c;
